import { ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output, inject, DestroyRef } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProcessMapService } from './process-map.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-process-map',
    templateUrl: './process-map.component.html',
    styleUrls: ['./process-map.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ProcessMapComponent {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  @Input('base-urls')
  set _base_urls(v: string) {
    try {
      const u = safeJsonInput<any>(v || '{}', null, 'process-map.parse1');
      if (u && Object.keys(u).length > 0) {
        this.baseURLs = u;
        this.tryInit();
      }
    } catch (_) {}
  }

  @Input('header-data')
  set _header_data(v: string) {
    try {
      const header = safeJsonInput<any>(v || '{}', null, 'process-map.parse2');
      if (header && Object.keys(header).length > 0) {
        this.globalService = header.globalParams;
        this.GENERAL_CONFIG = header.generalConfigData;
        this.userSettings = header.userSettingsData;
        this.tryInit();
      }
    } catch (_) {}
  }

  @Input('row-data')
  set _row_data(v: string) {
    try {
      this.rowData = safeJsonInput<any>(v || '{}', null, 'process-map.parse3');
      this.tryInit();
    } catch (_) {}
  }

  @Input('filter-params')
  set _filter_params(v: string) {
    try {
      const params = safeJsonInput<any>(v || '{}', null, 'process-map.parse4');
      this.filterParams = params;
      this.tryInit();
    } catch (_) {}
  }

  baseURLs: any = {};
  headerData: any = {};
  rowData: any = {};
  filterParams: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  userSettings: any = {};

  clients: any[] = [];
  client_key: string = '';
  taxPlacements: any[] = [];
  tax_id: any = null;
  taxIdLoader = false;
  mappedProcessesList: any[] = [];
  tasksList: any[] = [];
  original_tasksList: any[] = [];
  original_mappProcessedList: any[] = [];
  showBlock = false;
  showProcLoader = true;
  defaultClient = false;
  createProcBlock = false;
  newTaxPlacemat = false;
  processName = '';
  tax_year_begin: Date | null = null;
  tax_year_end: Date | null = null;
  saveloader = false;
  current_process = 0;
  selectedProc: number | null = null;
  showCreateTaxPlacematModal = false;
  showDummyActivityModal = false;
  showReorderModal = false;
  taxPlacematName = '';
  dummyActivityName = '';
  dummySelectedPlacemat: any = null;
  reorderProcesses: any[] = [];
  dateOptions: any = { dateDisabled: false, formatYear: 'yyyy', startingDay: 1, showWeeks: false };

  private isInitialized = false;

  constructor(
    private processMapService: ProcessMapService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef
  ) {}

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.GENERAL_CONFIG || Object.keys(this.GENERAL_CONFIG).length === 0) return;
    if (!this.baseURLs || Object.keys(this.baseURLs).length === 0) return;
    this.isInitialized = true;
    this.clients = this.userSettings?.user?.clients || [];
    this.client_key = this.userSettings?.user?.client?.client_key ?? this.client_key ?? '';
    if (this.client_key) {
      this.getTaxPlacematsByClientKey(this.client_key);
    }
    this.cdr.detectChanges();
  }

  get baseUrl(): string {
    return (this.GENERAL_CONFIG?.base_url ?? this.baseURLs?.api) || '';
  }

  get adminBaseUrl(): string {
    return this.baseURLs?.admin || this.GENERAL_CONFIG?.admin_api_url || '';
  }

  getData(): void {
    this.defaultClient = false;
    this.showBlock = false;
    this.showProcLoader = true;
    if (this.tax_id?.placematId != null) {
      this.getMappedProcessesByClientKey(this.client_key, this.tax_id.placematId);
    }
    this.cdr.detectChanges();
  }

  getTaxPlacematsByClientKey(client_key: string, tx_name?: string): void {
    this.taxIdLoader = true;
    this.cdr.detectChanges();
    const base = this.baseUrl || this.adminBaseUrl;
    this.processMapService.getTaxPlacematList(base, client_key).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        this.taxIdLoader = false;
        if (data?.callSuccess === '1' && data?.data) {
          this.taxPlacements = data.data;
          if (tx_name !== undefined) {
            const found = this.taxPlacements.find((t: any) => t.placematName === tx_name);
            if (found) this.tax_id = found;
            this.getData();
          } else {
            this.tax_id = this.taxPlacements[0] ?? null;
            if (this.tax_id) {
              this.getMappedProcessesByClientKey(this.client_key, this.tax_id.placematId);
              this.getTasksHierarchyByClientKey(this.client_key);
            }
          }
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.taxIdLoader = false;
        this.gtwSnackbarService.openSnackBar('An error has occurred loading tax placemats.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      },
    });
  }

  getMappedProcessesByClientKey(key: string, id: number | string): void {
    const base = this.baseUrl || this.adminBaseUrl;
    this.processMapService.getMappedProcesses(base, key, id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        if (data?.callSuccess === '1' && data?.data?.length > 0) {
          data.data.forEach((m: any) => {
            m.process_from = m.process_from ? new Date(m.process_from) : null;
            m.process_to = m.process_to ? new Date(m.process_to) : null;
            m.children = m.values || [];
            m.edit_delete = false;
            m.edit = false;
            m.children.forEach((c: any) => {
              c.selected = true;
              c.activity_name = c.name ?? c.activity_name;
              c.activity_key = c.children?.[0]?.activity_key ?? c.activity_key;
            });
          });
          this.original_mappProcessedList = this.deepClone(data.data);
          this.mappedProcessesList = this.deepClone(this.original_mappProcessedList);
          if (this.tasksList?.length) {
            this.defaultSelection();
          }
        } else {
          this.showProcLoader = false;
          this.defaultClient = true;
          this.mappedProcessesList = [];
          this.gtwSnackbarService.openSnackBar('No data available for selected client', 'gtw-snackbar--yellow');
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.showProcLoader = false;
        this.gtwSnackbarService.openSnackBar('An error has occurred.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      },
    });
  }

  getTasksHierarchyByClientKey(key: string): void {
    this.processMapService.getTasksHierarchy(this.adminBaseUrl, key).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        if (data && Array.isArray(data) && data.length > 0) {
          data.forEach((task: any) => {
            task.toggle = false;
            task.selected = false;
            task.selectedTaskCount = 0;
            (task.children || []).forEach((a: any) => {
              a.toggle = false;
              a.selected = false;
              a.draggable = true;
              a.subtask = false;
            });
            if (task.sub_task) {
              task.sub_task.forEach((st: any) => {
                st.activity_name = st.task_name;
                st.toggle = false;
                st.selected = false;
                st.draggable = false;
                st.subtask = true;
                st.selectActivityCount = 0;
                (st.children || []).forEach((s: any) => {
                  s.toggle = false;
                  s.selected = false;
                  s.draggable = true;
                });
                task.children = task.children || [];
                task.children.unshift(st);
              });
            }
          });
          this.original_tasksList = this.deepClone(data);
          this.tasksList = this.deepClone(this.original_tasksList);
          if (this.mappedProcessesList?.length) {
            this.defaultSelection();
          }
        } else {
          this.defaultClient = false;
          this.gtwSnackbarService.openSnackBar('Tasks Hierarchy Data is not available', 'gtw-snackbar--yellow');
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.gtwSnackbarService.openSnackBar('An error has occurred loading tasks.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      },
    });
  }

  private defaultSelection(): void {
    this.showProcLoader = false;
    this.showBlock = true;
    this.current_process = 0;
    this.selectedProc = 0;
    if (this.mappedProcessesList?.length > 0) {
      this.selectedProcess(this.mappedProcessesList[0], 0);
    }
    this.cdr.detectChanges();
  }

  resetData(clientKey: string): void {
    this.mappedProcessesList = [];
    this.tasksList = [];
    this.current_process = 0;
    this.selectedProc = null;
    this.defaultClient = false;
    this.showBlock = false;
    this.showProcLoader = false;
    this.createProcBlock = false;
    this.processName = '';
    this.newTaxPlacemat = false;
    this.client_key = clientKey;
    this.getTaxPlacematsByClientKey(this.client_key);
    this.cdr.detectChanges();
  }

  createType(type: string): void {
    (this as any)[type] = true;
    this.defaultClient = false;
    this.cdr.detectChanges();
  }

  cancelType(type: string, arr: string[]): void {
    (this as any)[type] = false;
    arr.forEach((prop) => ((this as any)[prop] = ''));
    this.cdr.detectChanges();
  }

  refresh(): void {
    this.requestGridUpdate.emit('refresh');
  }

  openCreateTaxPlacematModal(): void {
    this.taxPlacematName = '';
    this.showCreateTaxPlacematModal = true;
    this.cdr.detectChanges();
  }

  closeCreateTaxPlacematModal(): void {
    this.showCreateTaxPlacematModal = false;
    this.cdr.detectChanges();
  }

  createTaxPlacemat(): void {
    const name = (this.taxPlacematName || '').trim();
    if (!name) {
      this.gtwSnackbarService.openSnackBar('Please enter tax placemat name', 'gtw-snackbar--yellow');
      return;
    }
    const newTp = { placematId: 0, placematName: name };
    this.taxPlacements.push(newTp);
    this.tax_id = newTp;
    this.mappedProcessesList = [];
    this.showBlock = false;
    this.defaultClient = true;
    this.newTaxPlacemat = true;
    this.showCreateTaxPlacematModal = false;
    this.cdr.detectChanges();
  }

  deleteTaxPlacemat(): void {
    if (!this.tax_id) {
      return;
    }
    if (!this.tax_id.placematId || Number(this.tax_id.placematId) === 0) {
      this.taxPlacements = this.taxPlacements.filter((tp) => tp !== this.tax_id);
      this.tax_id = this.taxPlacements.length > 0 ? this.taxPlacements[0] : null;
      this.showBlock = false;
      this.defaultClient = true;
      this.newTaxPlacemat = false;
      this.cdr.detectChanges();
      return;
    }
    this.showBlock = false;
    this.showProcLoader = true;
    this.processMapService.deleteTaxPlacemat(this.adminBaseUrl, this.client_key, this.tax_id.placematId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        if (data?.callSuccess === '1') {
          this.gtwSnackbarService.openSnackBar('Tax Placemat Deleted Successfully.', 'gtw-snackbar--green');
          this.taxPlacements = this.taxPlacements.filter((tp: any) => tp.placematId !== this.tax_id.placematId);
          this.tax_id = this.taxPlacements.length > 0 ? this.taxPlacements[0] : null;
          this.defaultClient = true;
          this.showProcLoader = false;
        } else {
          this.gtwSnackbarService.openSnackBar(data?.errorMessage || 'Failed to delete tax placemat.', 'gtw-snackbar--red');
          this.showProcLoader = false;
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.showProcLoader = false;
        this.gtwSnackbarService.openSnackBar('Failed to delete tax placemat.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    });
  }

  addProc(procName: string): void {
    const name = (procName || '').trim();
    if (!name || !this.tax_year_begin || !this.tax_year_end) {
      this.gtwSnackbarService.openSnackBar('Process name and date range are required.', 'gtw-snackbar--yellow');
      return;
    }
    this.createProcBlock = false;
    const prevIndex = this.current_process;
    const newProc = {
      process_id: 0,
      label: name,
      num: this.mappedProcessesList.length + 1,
      process_from: this.tax_year_begin,
      process_to: this.tax_year_end,
      noActivity: true,
      edit_delete: true,
      edit: false,
      children: []
    };
    this.current_process = this.mappedProcessesList.length;
    this.mappedProcessesList.push(newProc);
    this.selectedProc = this.current_process;
    this.tasksList = this.deepClone(this.original_tasksList);
    this.tax_year_begin = null;
    this.tax_year_end = null;
    this.processName = '';
    if (this.mappedProcessesList.length > 1 && this.mappedProcessesList[prevIndex]) {
      this.mappedProcessesList[prevIndex].edit_delete = false;
      this.mappedProcessesList[prevIndex].edit = false;
    }
    this.showBlock = true;
    this.defaultClient = false;
    this.cdr.detectChanges();
  }

  selectedProcess(process: any, index?: number): void {
    const prev = this.current_process;
    if (index !== undefined && index !== null) {
      this.current_process = index;
    }
    if (this.mappedProcessesList[prev] && prev !== this.current_process) {
      this.mappedProcessesList[prev].edit_delete = false;
      this.mappedProcessesList[prev].edit = false;
    }
    if (this.mappedProcessesList[this.current_process]) {
      this.mappedProcessesList[this.current_process].edit_delete = true;
    }
    this.selectedProc = this.current_process;
    this.tasksList = this.checkActivities(process || this.mappedProcessesList[this.current_process]);
    this.checkParent(this.tasksList);
    this.noActivityCheck();
    this.cdr.detectChanges();
  }

  editProcess(index: number): void {
    if (this.mappedProcessesList[index]) {
      this.mappedProcessesList[index].edit = !this.mappedProcessesList[index].edit;
      this.cdr.detectChanges();
    }
  }

  cancelEdit(index: number): void {
    if (this.original_mappProcessedList[index]) {
      const original = this.deepClone(this.original_mappProcessedList[index]);
      this.mappedProcessesList[index].label = original.label;
      this.mappedProcessesList[index].process_from = original.process_from;
      this.mappedProcessesList[index].process_to = original.process_to;
    }
    this.editProcess(index);
  }

  saveProcess(process: any, index: number): void {
    const payload = {
      client_id: this.client_key,
      process_id: process.process_id,
      label: process.label,
      process_from: this.formatDate(process.process_from),
      process_to: this.formatDate(process.process_to)
    };
    this.processMapService.saveUpdatedProcesses(this.adminBaseUrl, JSON.stringify(payload), this.tax_id.placematId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        if (data?.callSuccess === 1) {
          this.gtwSnackbarService.openSnackBar('Data saved Successfully.', 'gtw-snackbar--green');
        } else {
          this.gtwSnackbarService.openSnackBar(data?.errorMessage || 'Failed to update process.', 'gtw-snackbar--red');
        }
        this.editProcess(index);
      },
      error: () => {
        this.gtwSnackbarService.openSnackBar('Failed to update process.', 'gtw-snackbar--red');
        this.editProcess(index);
      }
    });
  }

  deleteProc(procId: any, index: number): void {
    if (!procId || Number(procId) === 0) {
      this.mappedProcessesList.splice(index, 1);
      if (this.mappedProcessesList.length > 0) {
        this.current_process = 0;
        this.selectedProc = 0;
        this.selectedProcess(this.mappedProcessesList[0], 0);
      } else {
        this.showBlock = false;
        this.defaultClient = true;
      }
      this.cdr.detectChanges();
      return;
    }
    this.processMapService.deleteMappedProcesses(this.adminBaseUrl, this.client_key, procId, this.tax_id.placematId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        if (data?.callSuccess === '1') {
          this.gtwSnackbarService.openSnackBar('Process Deleted Successfully.', 'gtw-snackbar--green');
          this.mappedProcessesList.splice(index, 1);
          if (this.mappedProcessesList.length > 0) {
            this.current_process = 0;
            this.selectedProc = 0;
            this.selectedProcess(this.mappedProcessesList[0], 0);
          } else {
            this.showBlock = false;
            this.defaultClient = true;
          }
        } else {
          this.gtwSnackbarService.openSnackBar(data?.errorMessage || 'Failed to delete process.', 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.gtwSnackbarService.openSnackBar('Failed to delete process.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    });
  }

  openDummyActivityModal(): void {
    this.dummyActivityName = '';
    this.dummySelectedPlacemat = this.tax_id;
    this.showDummyActivityModal = true;
    this.cdr.detectChanges();
  }

  closeDummyActivityModal(): void {
    this.showDummyActivityModal = false;
    this.cdr.detectChanges();
  }

  createDummyActivity(): void {
    const name = (this.dummyActivityName || '').trim();
    if (!name || !this.mappedProcessesList[this.current_process]) {
      this.gtwSnackbarService.openSnackBar('Dummy activity name is required.', 'gtw-snackbar--yellow');
      return;
    }
    const activity = {
      task_key: 0,
      activity_key: 0,
      activity_name: name,
      toggle: false,
      selected: true,
      draggable: true,
      subtask: false,
      dummy_activity: true,
      placemat_id: this.dummySelectedPlacemat?.placematId || this.tax_id?.placematId || 0,
      placemat_name: this.dummySelectedPlacemat?.placematName || this.tax_id?.placematName || ''
    };
    this.mappedProcessesList[this.current_process].children.push(activity);
    this.mappedProcessesList[this.current_process].noActivity = false;
    this.showDummyActivityModal = false;
    this.cdr.detectChanges();
  }

  reorderProcessModal(): void {
    this.reorderProcesses = this.deepClone(this.mappedProcessesList);
    this.showReorderModal = true;
    this.cdr.detectChanges();
  }

  closeReorderModal(): void {
    this.showReorderModal = false;
    this.cdr.detectChanges();
  }

  moveReorderUp(index: number): void {
    if (index <= 0) {
      return;
    }
    const temp = this.reorderProcesses[index - 1];
    this.reorderProcesses[index - 1] = this.reorderProcesses[index];
    this.reorderProcesses[index] = temp;
  }

  moveReorderDown(index: number): void {
    if (index >= this.reorderProcesses.length - 1) {
      return;
    }
    const temp = this.reorderProcesses[index + 1];
    this.reorderProcesses[index + 1] = this.reorderProcesses[index];
    this.reorderProcesses[index] = temp;
  }

  applyReorder(): void {
    this.mappedProcessesList = this.deepClone(this.reorderProcesses);
    this.showReorderModal = false;
    if (this.mappedProcessesList.length > 0) {
      this.selectedProcess(this.mappedProcessesList[0], 0);
    }
    this.cdr.detectChanges();
  }

  saveTasks(): void {
    this.saveloader = true;
    const modified: any[] = [];
    const emptyProcesses: string[] = [];

    this.mappedProcessesList.forEach((element: any, index: number) => {
      const process: any = {
        process_id: element.process_id,
        client_id: this.client_key,
        label: element.label,
        num: index + 1,
        process_from: this.formatDate(element.process_from),
        process_to: this.formatDate(element.process_to),
        values: []
      };
      if (Array.isArray(element.children) && element.children.length > 0) {
        element.children.forEach((child: any, childIndex: number) => {
          if (child.dummy_activity) {
            process.values.push({
              activity_key: child.activity_key,
              activity_order: childIndex + 1,
              activity_name: child.activity_name,
              placemat_id: child.placemat_id
            });
          } else {
            process.values.push({
              activity_key: child.activity_key,
              activity_order: childIndex + 1
            });
          }
        });
      } else {
        emptyProcesses.push(element.label);
      }
      modified.push(process);
    });

    if (emptyProcesses.length > 0) {
      this.saveloader = false;
      this.gtwSnackbarService.openSnackBar(
        `Please add at least one activity in these processes to save: ${emptyProcesses.join(', ')}`,
        'gtw-snackbar--yellow'
      );
      this.cdr.detectChanges();
      return;
    }

    this.processMapService.saveMappedProcesses(
      this.adminBaseUrl,
      JSON.stringify(modified),
      this.client_key,
      this.tax_id?.placematId ?? 0,
      this.tax_id?.placematName ?? ''
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        this.saveloader = false;
        if (data?.callSuccess === 1) {
          this.gtwSnackbarService.openSnackBar('Data saved Successfully.', 'gtw-snackbar--green');
          if (this.tax_id && this.taxPlacements.length > 0 && this.newTaxPlacemat && data?.data) {
            this.tax_id.placematId = data.data;
            this.taxPlacements[this.taxPlacements.length - 1].placematId = data.data;
          }
          this.newTaxPlacemat = false;
          this.showBlock = false;
          this.showProcLoader = true;
          this.createProcBlock = false;
          this.processName = '';
          this.tax_year_begin = null;
          this.tax_year_end = null;
          this.getMappedProcessesByClientKey(this.client_key, this.tax_id?.placematId);
        } else {
          this.gtwSnackbarService.openSnackBar(data?.errorMessage || 'Failed to save process map.', 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.saveloader = false;
        this.gtwSnackbarService.openSnackBar('Failed to save process map.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    });
  }

  clearTasks(): void {
    this.current_process = 0;
    this.mappedProcessesList = this.deepClone(this.original_mappProcessedList);
    this.selectedProc = this.current_process;
    if (this.mappedProcessesList[0]) {
      this.mappedProcessesList[0].edit_delete = true;
      this.selectedProcess(this.mappedProcessesList[0], 0);
    }
    this.cdr.detectChanges();
  }

  toggleActivities(index: number): void {
    if (this.tasksList[index]) {
      this.tasksList[index].toggle = !this.tasksList[index].toggle;
      this.cdr.detectChanges();
    }
  }

  updateSelected(node: any, rel: 'parent' | 'child' | 'subchild', activity?: any, subactivity?: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (!this.mappedProcessesList[this.current_process]) {
      return;
    }
    if (rel === 'parent') {
      this.selectBranch(node, !!node.selected);
    } else if (rel === 'child' && activity) {
      if (activity.selected) {
        activity.status = 'on';
        if (activity.subtask) {
          (activity.children || []).forEach((subchild: any) => {
            subchild.status = 'on';
            subchild.selected = true;
            this.mappedProcessesList[this.current_process].children.push(subchild);
          });
        } else {
          this.mappedProcessesList[this.current_process].children.push(activity);
        }
      } else {
        if (activity.subtask) {
          (activity.children || []).forEach((subchild: any) => {
            subchild.selected = false;
            this.mappedProcessesList[this.current_process].children = this.removeActivities(
              this.mappedProcessesList[this.current_process].children,
              subchild.activity_key
            );
          });
        } else {
          this.mappedProcessesList[this.current_process].children = this.removeActivities(
            this.mappedProcessesList[this.current_process].children,
            activity.activity_key
          );
        }
      }
    } else if (rel === 'subchild' && subactivity) {
      if (subactivity.selected) {
        subactivity.status = 'on';
        this.mappedProcessesList[this.current_process].children.push(subactivity);
      } else {
        this.mappedProcessesList[this.current_process].children = this.removeActivities(
          this.mappedProcessesList[this.current_process].children,
          subactivity.activity_key
        );
      }
    }
    this.mappedProcessesList[this.current_process].children = this.uniqueActivities(this.mappedProcessesList[this.current_process].children);
    this.noActivityCheck();
    this.checkParent(this.tasksList);
    this.cdr.detectChanges();
  }

  private selectBranch(node: any, value: boolean): void {
    this.setTreeAttribute(node.children || [], value);
  }

  private setTreeAttribute(collection: any[], value: boolean): void {
    let dupMapped = this.deepClone(this.mappedProcessesList[this.current_process].children || []);
    collection.forEach((child: any) => {
      child.selected = value;
      if (child.subtask) {
        (child.children || []).forEach((subchild: any) => {
          subchild.selected = value;
        });
      }
    });
    if (value) {
      collection.forEach((child: any) => {
        child.status = 'on';
        if (child.subtask) {
          (child.children || []).forEach((subchild: any) => {
            subchild.status = 'on';
            dupMapped.push(subchild);
          });
        } else {
          dupMapped.push(child);
        }
      });
      this.mappedProcessesList[this.current_process].children = this.uniqueActivities(dupMapped);
      this.mappedProcessesList[this.current_process].noActivity = false;
    } else {
      collection.forEach((child: any) => {
        dupMapped = this.removeActivities(dupMapped, child.activity_key);
        if (child.subtask) {
          (child.children || []).forEach((subchild: any) => {
            dupMapped = this.removeActivities(dupMapped, subchild.activity_key);
          });
        }
      });
      this.mappedProcessesList[this.current_process].children = dupMapped;
      this.mappedProcessesList[this.current_process].noActivity = dupMapped.length === 0;
    }
  }

  private checkActivities(process: any): any[] {
    const duplicateList = this.deepClone(this.original_tasksList);
    (process?.children || []).forEach((mapped: any) => {
      const mappedKey = String(mapped.activity_key);
      duplicateList.forEach((task: any) => {
        (task.children || []).forEach((activity: any) => {
          if (String(activity.activity_key) === mappedKey) {
            activity.selected = true;
          }
          if (activity.subtask) {
            (activity.children || []).forEach((subchild: any) => {
              if (String(subchild.activity_key) === mappedKey) {
                subchild.selected = true;
              }
            });
          }
        });
      });
    });
    return duplicateList;
  }

  private checkParent(tasksList: any[]): void {
    tasksList.forEach((task: any) => {
      let childCount = 0;
      task.selected = true;
      (task.children || []).forEach((child: any) => {
        let subChildCount = 0;
        if (child.subtask) {
          child.selected = true;
          (child.children || []).forEach((subchild: any) => {
            if (!subchild.selected) {
              child.selected = false;
              task.selected = false;
            } else {
              subChildCount += 1;
            }
          });
          child.selectActivityCount = subChildCount;
        }
        if (!child.selected) {
          task.selected = false;
        } else {
          childCount += 1;
        }
      });
      task.selectedTaskCount = childCount;
    });
  }

  private noActivityCheck(): void {
    const process = this.mappedProcessesList[this.current_process];
    if (process) {
      process.noActivity = !(process.children && process.children.length > 0);
    }
  }

  private removeActivities(arr: any[], key: any): any[] {
    return (arr || []).filter((act: any) => String(act.activity_key) !== String(key));
  }

  private uniqueActivities(arr: any[]): any[] {
    const map: Record<string, any> = {};
    (arr || []).forEach((item: any) => {
      map[String(item.activity_key)] = item;
    });
    return Object.keys(map).map((k) => map[k]);
  }

  private formatDate(value: any): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return '';
    }
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }

  private deepClone<T>(value: T): T {
    return safeJsonInput<any>(JSON.stringify(value), null, 'process-map.parse5');
  }

  getSourceListId(taskIndex: number, activityIndex?: number): string {
    return activityIndex === undefined
      ? `task-children-${taskIndex}`
      : `task-subchildren-${taskIndex}-${activityIndex}`;
  }

  getConnectedDropListIds(): string[] {
    const ids = ['mapped-process-drop-list'];
    (this.tasksList || []).forEach((task: any, tIndex: number) => {
      ids.push(this.getSourceListId(tIndex));
      (task.children || []).forEach((activity: any, aIndex: number) => {
        if (activity.subtask) {
          ids.push(this.getSourceListId(tIndex, aIndex));
        }
      });
    });
    return ids;
  }

  dropMapped(event: CdkDragDrop<any[]>): void {
    if (!this.mappedProcessesList[this.current_process]) {
      return;
    }
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      this.cdr.detectChanges();
      return;
    }
    const dragged = event.previousContainer.data[event.previousIndex];
    if (!dragged) {
      return;
    }
    const mappedCopy = this.toMappedActivity(dragged);
    const currentChildren = this.mappedProcessesList[this.current_process].children || [];
    const exists = currentChildren.some((c: any) => String(c.activity_key) === String(mappedCopy.activity_key));
    if (!exists) {
      currentChildren.push(mappedCopy);
      this.mappedProcessesList[this.current_process].children = currentChildren;
    }
    this.markActivitySelectionByKey(mappedCopy.activity_key, true);
    this.noActivityCheck();
    this.checkParent(this.tasksList);
    this.cdr.detectChanges();
  }

  dropSource(event: CdkDragDrop<any[]>): void {
    if (event.previousContainer === event.container) {
      return;
    }
    if (event.previousContainer.id === 'mapped-process-drop-list') {
      const dragged = event.previousContainer.data[event.previousIndex];
      const activityKey = dragged?.activity_key;
      if (activityKey !== undefined && activityKey !== null) {
        this.mappedProcessesList[this.current_process].children = this.removeActivities(
          this.mappedProcessesList[this.current_process].children,
          activityKey
        );
        this.markActivitySelectionByKey(activityKey, false);
        this.noActivityCheck();
        this.checkParent(this.tasksList);
        this.cdr.detectChanges();
      }
    }
  }

  private toMappedActivity(item: any): any {
    return {
      ...item,
      selected: true,
      status: 'on',
      activity_name: item.activity_name || item.name || '',
      activity_key: item.activity_key
    };
  }

  private markActivitySelectionByKey(activityKey: any, selected: boolean): void {
    (this.tasksList || []).forEach((task: any) => {
      (task.children || []).forEach((activity: any) => {
        if (String(activity.activity_key) === String(activityKey)) {
          activity.selected = selected;
        }
        if (activity.subtask) {
          (activity.children || []).forEach((subchild: any) => {
            if (String(subchild.activity_key) === String(activityKey)) {
              subchild.selected = selected;
            }
          });
        }
      });
    });
  }
}
